import { Account } from "@common/types/domains/accounting/account";
import { useMemo } from "react";
import { getNonFinancialJournalEntryIndex } from "@common/helpers/transactions";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useAccounts } from "./useAccounts";

export interface TransactionForLedgerAccount {
    isBusiness: boolean;
    entity: { id: number };
    financialAccount?: { entityId: number | null } | null;
    journalEntries?: Array<{
        accountingLedgerId?: string;
        accountingLedger: {
            id?: string;
            basis: AccountingLedgerBasis;
            entityId: number;
        };
        lineOrder: number;
        accountId: string;
    }> | null;
    accountId?: string | null;
    amount: number;
}

interface UseTransactionLedgerAccountOptions {
    includeCurrentAccount?: boolean;
    currentAccountId?: string;
    basis: AccountingLedgerBasis;
    ledgerId?: string;
}

export function useTransactionLedgerAccount(
    transaction: TransactionForLedgerAccount,
    options: UseTransactionLedgerAccountOptions = {
        basis: AccountingLedgerBasis.cash,
    },
) {
    const entityIdForGLAccount = useEntityIdForGLAccount(transaction);

    const accountsData = useAccounts({
        entityId: entityIdForGLAccount,
        includeCurrentAccount: options.includeCurrentAccount ?? false,
        currentAccountId: options.currentAccountId,
        ledgerBasis: options.basis ?? AccountingLedgerBasis.cash,
    });

    const availableJournalEntries = useMemo(
        () =>
            (transaction.journalEntries ?? [])
                .filter((je) => {
                    if (je.accountingLedger.entityId !== entityIdForGLAccount) {
                        return false;
                    }
                    if (options.ledgerId) {
                        return (
                            (je.accountingLedgerId ??
                                je.accountingLedger.id) === options.ledgerId
                        );
                    }
                    return (
                        je.accountingLedger.basis ===
                        (options.basis ?? AccountingLedgerBasis.cash)
                    );
                })
                .sort((a, b) => a.lineOrder - b.lineOrder),
        [
            transaction.journalEntries,
            entityIdForGLAccount,
            options.basis,
            options.ledgerId,
        ],
    );

    const account = useMemo((): Account | null => {
        if (!entityIdForGLAccount) {
            return null;
        }

        const accountId =
            options.currentAccountId ??
            availableJournalEntries.at(
                getNonFinancialJournalEntryIndex(transaction),
            )?.accountId;

        return accountsData.accounts.find((a) => a.id === accountId) ?? null;
    }, [
        availableJournalEntries,
        accountsData.accounts,
        entityIdForGLAccount,
        transaction,
        options.currentAccountId,
    ]);

    return {
        entityIdForGLAccount,
        accountsData,
        availableJournalEntries,
        account,
        disableAccountSelect: entityIdForGLAccount !== transaction.entity.id,
    };
}
